// Service autonome de l'onglet Marché / Notifications-signature (acheteur MS).
// Routes /subsequents/* + /documents/* + /app/signature-mode. operationId/numero en argument
// (aucun sniffing d'URL). Les documents (PDF/zip) reviennent en Blob, pas en JSON.

const authHeaders = (json = true): HeadersInit => {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  };
};

const authedFetch = async <T = unknown>(path: string, options: RequestInit = {}): Promise<T> => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    ...options,
    headers: { ...authHeaders(), Accept: 'application/json', ...(options.headers || {}) },
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || `Erreur HTTP: ${response.status}`);
  }
  return response.json().catch(() => ({})) as Promise<T>;
};

const authedFetchBlob = async (path: string, body: object): Promise<Blob> => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    method: 'POST',
    headers: { ...authHeaders(), Accept: 'application/pdf' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
  return response.blob();
};

// --- Mode de signature -------------------------------------------------------
export type SignatureMode = 'manuscrite' | 'automatique';

interface SignatureModeResponse {
  ms_mode?: string;
  mode?: string;
}

// GET /app/signature-mode : mode configure (manuscrite | automatique). Lit ms_mode sinon mode.
export const getSignatureMode = async (): Promise<SignatureMode | null> => {
  const data = await authedFetch<SignatureModeResponse>('/app/signature-mode');
  const mode = data.ms_mode ?? data.mode;
  return mode === 'manuscrite' || mode === 'automatique' ? mode : null;
};

// Cache local du mode (l'API fait autorite ; le cache evite un flash avant la reponse).
const SIGNATURE_MODE_CACHE_KEY = 'ms_signature_mode';

export const readCachedSignatureMode = (): SignatureMode => {
  const cached = localStorage.getItem(SIGNATURE_MODE_CACHE_KEY);
  return cached === 'manuscrite' || cached === 'automatique' ? cached : 'automatique';
};

export const cacheSignatureMode = (mode: SignatureMode): void => {
  localStorage.setItem(SIGNATURE_MODE_CACHE_KEY, mode);
};

// --- Signataire / noms signés ------------------------------------------------
export interface MarcheSignataireInfo {
  signataire?: { prenom?: string; nom?: string; email?: string; telephone?: string };
}

// GET /subsequents/signature/:id/signataire-info : prenom + nom reels du signataire acheteur.
export const getMarcheSignataireInfo = (operationId: number | string): Promise<MarcheSignataireInfo> =>
  authedFetch<MarcheSignataireInfo>(`/subsequents/signature/${operationId}/signataire-info`);

export interface SignedNamesResponse {
  acheteur?: { signataire_nom?: string };
  prestataire?: { signataire_nom?: string };
}

// GET /subsequents/operation/:id/signed-names?commande_id= : noms reellement utilises a la signature.
export const getSignedNames = (
  operationId: number | string,
  commandeId?: number | string,
): Promise<SignedNamesResponse> => {
  const params = commandeId !== undefined && commandeId !== null ? `?commande_id=${commandeId}` : '';
  return authedFetch<SignedNamesResponse>(`/subsequents/operation/${operationId}/signed-names${params}`);
};

// --- Signature + demande -----------------------------------------------------
export interface RequestSignaturePayload {
  operation_id: number;
  signataire_id: number;
  signataire_type?: string;
}

// POST /subsequents/request-signature : l'acheteur simple demande la signature par mail.
export const requestSignature = (payload: RequestSignaturePayload): Promise<unknown> =>
  authedFetch('/subsequents/request-signature', { method: 'POST', body: JSON.stringify(payload) });

export interface SignPublicPayload {
  operation_id: number;
  commande_id: number;
}

// POST /subsequents/public/sign/ : signature acheteur (organisme) en mode automatique.
export const signPublic = (payload: SignPublicPayload): Promise<unknown> =>
  authedFetch('/subsequents/public/sign/', { method: 'POST', body: JSON.stringify(payload) });

// --- Notification de decision ------------------------------------------------
export interface NotificationPayload {
  operation_id: number;
  commande_id: number;
  selected_commande_id: number;
}

// POST /subsequents/notifications/send : notifie un prestataire (retenu si commande_id===selected).
export const sendNotification = (payload: NotificationPayload): Promise<unknown> =>
  authedFetch('/subsequents/notifications/send', { method: 'POST', body: JSON.stringify(payload) });

// --- Documents (Blob) --------------------------------------------------------
// POST /documents/signed-contrat : PDF du MS (preview ET telechargement signe). external_id = numero.
export const fetchSignedContratBlob = (externalId: string | number): Promise<Blob> =>
  authedFetchBlob('/documents/signed-contrat', {
    external_id: externalId,
    external_type: 'operation',
    source: 'MS',
  });

// POST /documents/audit-trail : zip des preuves de signature.
export const fetchAuditTrailBlob = (externalId: string | number): Promise<Blob> =>
  authedFetchBlob('/documents/audit-trail', {
    external_id: externalId,
    external_type: 'operation',
  });

// Helper navigateur : declenche le telechargement d'un Blob (remplace les <a download> inline).
export const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
